import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DeleteOutlined, EyeOutlined, ReloadOutlined, SyncOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Badge, Button, Card, DatePicker, Divider, Modal, Tabs, Tag, Tooltip, Typography } from 'antd';
import moment from 'moment';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar';
import DynamicTable from '@/components/DynamicTable';
import BaseSelect from '@/components/Elements/BaseSelect';
import { checkPermission } from '@/utils/permission';
import {
    cleanObject,
    ConvertRewardType,
    getFirstLetterOfWords,
    isValidDate,
    RewardType,
    stripHtml
} from '@/utils/helpers';
import {
    getListRewardHistories,
    getSpecificRewardHistories,
    removeRewardHistories,
    spinReward
} from '@/redux/actions/rewardSpinActions';
import { getListProvinces, getListWards } from '@/redux/actions/regionsActions';
import RewardSpinDetail from './RewardSpinDetail';

const { RangePicker } = DatePicker;
const { Paragraph } = Typography;
const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/reward-histories', breadcrumbName: 'Quay thưởng' }
];

class RewardSpinList extends Component {
    state = {
        isLoading: false,
        selectedRowKeys: [],
        wards: [],
        isOpenDetail: false,
        currentItem: null,
        currentId: null,
        loadingDetail: false
    };

    componentDidMount() {
        const query = this.getQuery();
        if (!query.tab) {
            this.pushQuery({ ...query, tab: 'reward_current' });
        } else {
            this.loadData(query);
        }
        this.props.getListProvinces();
        if (query.province_code) this.loadWards(query.province_code);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.search !== this.props.location.search) {
            const query = this.getQuery();
            this.setState({ selectedRowKeys: [] });
            this.loadData(query);
            if (query.province_code) this.loadWards(query.province_code);
            else this.setState({ wards: [] });
        }
    }

    getQuery = () => qs.parse(this.props.location.search, { ignoreQueryPrefix: true });

    pushQuery = query => this.props.history.push({
        pathname: this.props.location.pathname,
        search: qs.stringify(cleanObject({ ...query, page: 1 }))
    });

    loadData = query => {
        this.setState({ isLoading: true });
        return this.props.getListRewardHistories(query)
            .finally(() => this.setState({ isLoading: false }));
    };

    loadWards = provinceCode => {
        if (!provinceCode) {
            this.setState({ wards: [] });
            return Promise.resolve();
        }
        return this.props.getListWards({ province_code: provinceCode })
            .then(response => this.setState({ wards: response.data || [] }));
    };

    changeFilter = (name, value) => {
        const query = { ...this.getQuery(), [name]: value };
        if (name === 'province_code') delete query.wards_code;
        this.pushQuery(query);
    };

    changeDateRange = dates => {
        const query = { ...this.getQuery() };
        if (dates?.length === 2) {
            query.created_date_start = dates[0].format('YYYY-MM-DD');
            query.created_date_end = dates[1].format('YYYY-MM-DD');
        } else {
            delete query.created_date_start;
            delete query.created_date_end;
        }
        this.pushQuery(query);
    };

    viewDetail = id => {
        this.setState({ currentId: id, loadingDetail: true });
        this.props.getSpecificRewardHistories(id)
            .then(currentItem => this.setState({ isOpenDetail: true, currentItem }))
            .finally(() => this.setState({ loadingDetail: false }));
    };

    remove = ids => this.props.removeRewardHistories(ids, this.getQuery().tab).then(() => {
        this.setState({ selectedRowKeys: [] });
        return this.loadData(this.getQuery());
    });

    confirmRemove = ids => Modal.confirm({
        title: 'Xóa kết quả quay thưởng',
        content: 'Bạn chắc chắn muốn xóa các bản ghi đã chọn?',
        okText: 'Xóa',
        okType: 'danger',
        cancelText: 'Hủy',
        onOk: () => this.remove(ids)
    });

    confirmSpin = () => Modal.confirm({
        title: 'Thực hiện quay thưởng',
        content: 'Hệ thống sẽ tạo bộ giải mới theo cấu hình hiện tại. Bạn muốn tiếp tục?',
        okText: 'Quay thưởng',
        cancelText: 'Hủy',
        onOk: () => {
            this.setState({ isLoading: true });
            return this.props.spinReward()
                .then(() => this.loadData(this.getQuery()))
                .finally(() => this.setState({ isLoading: false }));
        }
    });

    renderCustomer = record => record.full_name ? (
        <span>
            {record.avatar ? (
                <Avatar size="small" icon={<UserOutlined />} src={record.avatar} />
            ) : (
                <Avatar size="small" style={{ backgroundColor: '#31cdf5' }}>
                    {getFirstLetterOfWords(record.full_name)}
                </Avatar>
            )}
            &nbsp;{record.full_name}
        </span>
    ) : '—';

    renderExpiry = (value, record) => {
        if (!value) return <Tag>N/A</Tag>;
        if (Number(record.status) === 1) {
            return <Badge count="Đã nhận thưởng" size="small" className="custom-badge" color="#096dd9">{isValidDate(value, false)}</Badge>;
        }

        const diffDays = moment(value).diff(moment().startOf('day'), 'days');
        const text = diffDays > 0 ? `Còn ${diffDays} ngày` : diffDays < 0 ? `${Math.abs(diffDays)} ngày trước` : 'Hết hạn hôm nay';
        const color = diffDays > 0 ? '#02805f' : diffDays < 0 ? 'red' : '#f50';
        return <Badge count={text} size="small" className="custom-badge" color={color}>{isValidDate(value, false)}</Badge>;
    };

    render() {
        const query = this.getQuery();
        const { rewards, pagination, provinces } = this.props;
        const { isLoading, selectedRowKeys, wards, isOpenDetail, currentItem, currentId, loadingDetail } = this.state;
        const isHistory = query.tab === 'reward_histories';
        const dateRange = query.created_date_start && query.created_date_end
            ? [moment(query.created_date_start, 'YYYY-MM-DD'), moment(query.created_date_end, 'YYYY-MM-DD')]
            : undefined;
        const columns = [
            {
                title: 'Thao tác', key: 'actions', width: 90, align: 'center', render: (_, record) => (
                    <>
                        {isHistory && <EyeOutlined onClick={() => this.viewDetail(record.id)} />}
                        {isHistory && checkPermission('reward_spin', 'remove') && <Divider type="vertical" />}
                        {checkPermission('reward_spin', 'remove') && (
                            <DeleteOutlined style={{ color: 'red' }} onClick={() => this.confirmRemove([record.id])} />
                        )}
                    </>
                )
            },
            { title: 'Họ và tên', dataIndex: 'full_name', key: 'full_name', width: 220, render: (_, record) => this.renderCustomer(record) },
            {
                title: 'Mã KH', dataIndex: 'code', key: 'code', sorter: true, width: 140,
                render: value => value ? <Paragraph style={{ margin: 0 }} copyable={{ text: value, tooltips: false }}>{value}</Paragraph> : '—'
            },
            {
                title: 'Mã thẻ', dataIndex: 'card_number_new', key: 'card_number_new', sorter: true, width: 170,
                render: value => value ? <Paragraph style={{ margin: 0 }} copyable={{ text: value, tooltips: false }}>{value}</Paragraph> : '—'
            },
            {
                title: 'Mã thẻ cũ', dataIndex: 'card_number', key: 'card_number', sorter: true, width: 170,
                render: value => value ? <Paragraph style={{ margin: 0 }} copyable={{ text: value, tooltips: false }}>{value}</Paragraph> : '—'
            },
            { title: 'Giải thưởng', dataIndex: 'type', key: 'type', sorter: true, width: 140, render: ConvertRewardType },
            { title: 'Ngày quay', dataIndex: 'created_at', key: 'created_at', sorter: true, width: 160, render: value => value ? isValidDate(value) : '—' },
            { title: 'Ngày hết hạn', dataIndex: 'expire_date', key: 'expire_date', sorter: true, width: 150, render: this.renderExpiry },
            { title: 'Ngày nhận thưởng', dataIndex: 'received_date', key: 'received_date', sorter: true, width: 170, render: value => value ? isValidDate(value) : '—' },
            { title: 'Tỉnh/Thành phố', dataIndex: 'province_name', key: 'province_name', sorter: true, width: 160, render: value => value || '—' },
            { title: 'Phường/Xã', dataIndex: 'wards_name', key: 'wards_name', sorter: true, width: 160, render: value => value || '—' },
            {
                title: 'Địa chỉ', dataIndex: 'address', key: 'address', width: 240,
                render: value => {
                    const text = stripHtml(value || '');
                    return text ? <Tooltip title={text}><span>{text}</span></Tooltip> : '—';
                }
            }
        ];

        return (
            <div>
                <PageTitle routes={routes} title="Quay thưởng" />
                <TableActionBar
                    isShowAddButton={false}
                    isShowDeleteButton={checkPermission('reward_spin', 'remove')}
                    disabled={!selectedRowKeys.length}
                    onDelete={() => this.confirmRemove(selectedRowKeys)}
                    onSearch={value => this.changeFilter('keyword', value)}
                    defaultKeyword={query.keyword}
                    activeFilter={Boolean(query.created_date_start || query.province_code || query.wards_code || query.type || query.status !== undefined)}
                    filters={[
                        <BaseSelect
                            key="province"
                            options={provinces}
                            optionValue="code"
                            optionLabel="name"
                            showSearch
                            placeholder="Tỉnh/Thành phố"
                            className="w-100"
                            value={query.province_code}
                            onChange={value => this.changeFilter('province_code', value)}
                        />,
                        <BaseSelect
                            key="ward"
                            options={wards}
                            optionValue="code"
                            optionLabel="name"
                            showSearch
                            placeholder="Phường/Xã"
                            className="w-100"
                            value={query.wards_code}
                            onChange={value => this.changeFilter('wards_code', value)}
                        />,
                        <RangePicker
                            key="date-range"
                            format="DD/MM/YYYY"
                            placeholder={['Ngày quay', 'Đến ngày']}
                            className="w-100"
                            value={dateRange}
                            onChange={this.changeDateRange}
                        />
                    ]}
                >
                    {checkPermission('reward_spin', 'create') && (
                        <Button className="table-button" icon={<SyncOutlined />} type="primary" loading={isLoading} onClick={this.confirmSpin}>Quay thưởng</Button>
                    )}
                    <BaseSelect
                        options={[{ label: 'Đã nhận thưởng', value: 1 }, { label: 'Chưa nhận thưởng', value: 0 }]}
                        placeholder="Trạng thái"
                        style={{ width: '150px' }}
                        className="table-button"
                        value={query.status !== undefined ? Number(query.status) : undefined}
                        onChange={value => this.changeFilter('status', value)}
                    />
                    <BaseSelect
                        options={RewardType || []}
                        showSearch
                        placeholder="Giải thưởng"
                        style={{ width: '150px' }}
                        className="table-button"
                        value={query.type}
                        onChange={value => this.changeFilter('type', value)}
                    />
                    <Button className="table-button" icon={<ReloadOutlined />} loading={isLoading} onClick={() => this.loadData(query)}>Làm mới</Button>
                    {selectedRowKeys.length > 0 && <span>Đang chọn {selectedRowKeys.length} bản ghi</span>}
                </TableActionBar>
                <Card size="small" bodyStyle={{ padding: 0 }}>
                    <Tabs activeKey={query.tab || 'reward_current'} onChange={tab => this.changeFilter('tab', tab)} type="card" size="small">
                        <Tabs.TabPane tab="Giải thưởng hiện tại" key="reward_current" />
                        <Tabs.TabPane tab="Lịch sử giải thưởng" key="reward_histories" />
                    </Tabs>
                    <div className="has-custom-column-table-btn">
                        <DynamicTable
                            moduleKey={`reward_spin_${query.tab || 'reward_current'}`}
                            columns={columns}
                            dataSource={rewards}
                            isLoading={isLoading}
                            pagination={pagination}
                            rowSelection={{ selectedRowKeys, onChange: keys => this.setState({ selectedRowKeys: keys }) }}
                        />
                    </div>
                </Card>
                <RewardSpinDetail
                    visible={isOpenDetail}
                    currentItem={currentItem}
                    loadingDetail={loadingDetail}
                    onCancel={() => this.setState({ isOpenDetail: false, currentItem: null })}
                    reloadData={() => this.viewDetail(currentId)}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({
    rewards: state.rewardSpinReducer.rewards.items,
    pagination: state.rewardSpinReducer.rewards.pagination,
    provinces: state.regionsReducer.list_provinces
});

const mapDispatchToProps = dispatch => ({
    getListRewardHistories: filter => dispatch(getListRewardHistories(filter)),
    getSpecificRewardHistories: id => dispatch(getSpecificRewardHistories(id)),
    removeRewardHistories: (ids, tab) => dispatch(removeRewardHistories(ids, tab)),
    spinReward: () => dispatch(spinReward()),
    getListProvinces: () => dispatch(getListProvinces()),
    getListWards: filter => dispatch(getListWards(filter))
});

export default connect(mapStateToProps, mapDispatchToProps)(RewardSpinList);
